import { FC, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Alert } from '@mui/material';
import { Box, Stack } from '@mui/system';

import ModalNotification from '@/shared/components/dialogs/ModalNotification';
import ProductVariant from '@/shared/types/products/productVariant.type';

import AssignProductsTable from '../../components/AssignProductsTable/AssignProductsTable';

type AssignProductsDialogProps = {
  assignedVariants?: ProductVariant[];
  errors?: string[];
  onClose: () => void;
  proceed: (values: ProductVariant[]) => void;
};

const AssignProductsDialog: FC<AssignProductsDialogProps> = ({
  assignedVariants,
  errors,
  onClose,
  proceed,
}) => {
  const { t } = useTranslation(['shipping', 'common']);

  const [tableDetails, setTableDetails] = useState({
    rows: [] as ProductVariant[],
    level: 'products',
  });

  const hasErrors = errors && errors.length > 0;

  return (
    <ModalNotification
      fullWidth
      size={hasErrors ? 'sm' : 'lg'}
      open
      onClose={onClose}
      dialogProps={{
        title: t('shipping:assignProductsAndVariants'),
        content: hasErrors ? (
          <Stack direction='column' gap={2}>
            {errors.map((error, index) => (
              <Alert key={index} severity='error'>
                {error}
              </Alert>
            ))}
          </Stack>
        ) : (
          <AssignProductsTable
            assignedVariants={assignedVariants}
            getSelectedRows={(rows) =>
              setTableDetails((prev) => ({ ...prev, rows }))
            }
            getSelectedLevel={(level) =>
              setTableDetails((prev) => ({ ...prev, level }))
            }
          />
        ),
        actions: hasErrors
          ? [
              {
                label: t('common:close'),
                variant: 'text',
                color: 'primary',
                onClick: onClose,
              },
            ]
          : [
              {
                label: t('common:close'),
                variant: 'text',
                color: 'primary',
                onClick: onClose,
              },
              {
                label: 'action-button',
                renderComponent: (
                  <Trans
                    i18nKey={
                      tableDetails.level === 'products'
                        ? 'shipping:assignSelectedProducts'
                        : 'shipping:assignSelectedVariants'
                    }
                    ns='shipping'
                    components={{
                      span: <Box component='span' sx={{ mr: 0.5 }} />,
                    }}
                    count={tableDetails.rows.length}
                  />
                ),
                disabled: tableDetails.rows.length === 0,
                variant: 'contained',
                color: 'primary',
                onClick: () => {
                  proceed(tableDetails.rows);
                },
              },
            ],
      }}
    />
  );
};

export default AssignProductsDialog;
